/**
 * LoginVerificationCode
 *
 * Single-input 6-digit verification code entry with iOS QuickType autofill support.
 *
 * Why single input (not 6 separate boxes): iOS QuickType's `autoComplete="one-time-code"`
 * targets a single focused input and inserts the entire 6-digit code at once. A multi-input
 * pattern can race with the focus-advance handler under fast typing and silently mangle the
 * concatenated value. Browser paste behavior on multi-input is also fragile when the pasted
 * text contains trailing whitespace from rich-text email apps. Single input eliminates both
 * failure modes.
 *
 * The visual still reads as a sequence of digit slots via the SCSS `letter-spacing` +
 * monospace font in `_login.scss:_verification-code-container`.
 *
 * Consumed by: ChallengeLoginModal, PolarisCodLoginModal, ThirdwebLogin.
 */

import {
    ChangeEvent,
    FormEvent,
    ReactElement,
    useEffect, useRef,
    useState,
} from "react";

interface Props {
    email: string;
    hasError: boolean;
    onRequestNewCode: () => void;
    onSubmit: (code: string) => void;
    brandColor?: string;
}

function LoginVerificationCode(props: Props): ReactElement {
    const [code, setCode] = useState<string>("");
    const [timer, setTimer] = useState<number>(60);
    const [focused, setFocused] = useState<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        inputRef.current?.focus();
        resetInterval();
        return () => clearInterval(intervalId.current);
    }, []);

    function codeOnChange(e: ChangeEvent<HTMLInputElement>): void {
        // Strip everything that isn't a digit, cap at 6
        const next = e.target.value.replace(/\D/g, "").slice(0, 6);
        setCode(next);
    }

    function verifyWithCode(e: FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (code.length !== 6) return;
        props.onSubmit(code);
    }

    function resetInterval() {
        setTimer(60);
        clearInterval(intervalId.current);
        intervalId.current = setInterval(() => {
            setTimer((time) => {
                if (time === 0) {
                    clearInterval(intervalId.current);
                    return 0;
                }
                return time - 1;
            });
        }, 1000);
    }

    function onRequestNewCode() {
        resetInterval();
        setCode("");
        props.onRequestNewCode();
    }

    return (
        <>
            <form onSubmit={verifyWithCode}>
                <p className="color-medium-gray text-center">
                    Enter the verification code sent to
                </p>
                <p className="text-center">
                    {props.email}
                </p>
                <div className="login-page_content_verification-code-container">
                    <input
                        ref={inputRef}
                        id="verification-code"
                        name="verification-code"
                        value={code}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        onChange={codeOnChange}
                        required={true}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        aria-label="6-digit verification code"
                        style={(focused || hovered) && props.brandColor ? {
                            borderColor: props.brandColor,
                            outline: "none",
                        } : undefined}
                    />
                </div>
                {props.hasError &&
                    <div className="login-page_content_verification-code-error">
                        Verification code failed.<br/>
                        Please try again or request a new code.
                    </div>
                }
                {/* Native <button> — the legacy button retired here.
                    When a `brandColor` is passed (Polaris / brand-themed
                    login modal), inline-style override drives the bg so
                    a hex prop can win across default/disabled states. */}
                <button
                    type="submit"
                    disabled={code.length !== 6}
                    className="login-page_content_verify-button"
                    style={props.brandColor ? {
                        backgroundColor: props.brandColor,
                        borderColor: props.brandColor,
                    } : undefined}
                >
                    Verify
                </button>
            </form>
            <div className="login-page_content_request-code">
                {timer > 0
                    ? <div className="login-page_content_request-code_timer">
                        Resend code in {timer} seconds
                    </div>
                    : <button
                        type="button"
                        className="login-page_content_request-code_button"
                        onClick={onRequestNewCode}
                    >
                        Request New Code
                    </button>
                }
            </div>
        </>
    );
}

export default LoginVerificationCode;
